import React, { useEffect, useState, useRef } from 'react';

import { useParams, useNavigate } from 'react-router-dom'; // Import useLocation

import { useFolder } from '../../context/folder-context'; // Custom hook to use folder context

import Container from '../../components/blocks/container';
import ApplianceInfo from '../../components/folder-components/appliance/folder--appliance-info';
import WarrantyInfo from '../../components/folder-components/folder--warranty-info';
import ClientInfo from '../../components/folder-components/folder--client-info';
import FolderInfo from '../../components/folder-components/folder--folder-info';
import PartsInfo from '../../components/folder-components/folder--parts-info';
import NavigateFooter from '../../components/folder-components/folder--navigate-footer';
import OverviewTools from '../../components/folder-components/tools/folder--overview--tools';
import DeclaredIssueInfo from '../../components/folder-components/issues/folder--declared-issue-info';
import AdditionalInfo from '../../components/folder-components/issues/folder--additional-info';
import AppointmentsInfo from '../../components/folder-components/appointments/folder--appointments-info';
import PaymentsOverview from '../../components/folder-components/payments/folder--payments--overview';
import foldersService from '../../services/FoldersService';
import appointmentsService from '../../services/AppointmentsService';
import ConfirmSendFolderModal from '../../components/folder-components/tools/modals/modal--folder--confirm-send';

/**
 * FolderOverviewPage Component
 * 
 * This component displays the folder overview page with all the information related to the folder.
 * @returns {JSX.Element} - Rendered component
 */
const FolderOverviewPage = () => {
    const { refFolder } = useParams(); // Get the folder number from the URL
    const navigate = useNavigate(); // Hook to programmatically navigate

    // Reference to the overview container
    const overviewRef = useRef();

    const { title, color } = useFolder(); // Get the title and color from the folder context

    const [ lockNavigation, setLockNavigation ] = useState(false); // State to lock navigation if there are unsent changes
    const [ confirmHomeOpen, setConfirmHomeOpen ] = useState(false);

    useEffect(() => {
        //Keep last appointment in state
        try {
            (async () => {
                const folder = await foldersService.getFolderByNumber(refFolder);

                if (!folder) {
                    console.error('Dossier introuvable');
                    return;
                }

                const folderId = folder.id;

                // Fetch appointments related to the folder
                const appointments = await appointmentsService.getAppointmentsByFolderId(folderId);

                if (appointments && appointments.length > 0) {
                    // Sort appointments by date descending
                    appointments.sort((a, b) => new Date(b.date) - new Date(a.date));
                    const lastAppointment = appointments[0];

                    // Check if the last appointment is unsent
                    if (lastAppointment && !lastAppointment.sent_by_email) {
                        setLockNavigation(true);
                    } else {
                        setLockNavigation(false);
                    }
                }
            })();
        } catch (error) {
            console.error('Erreur lors du chargement des interventions :', error);
        }
    }, [refFolder]);

    // If folder data is not available, return null or a loading indicator
    if (!refFolder) {
        return <div>Chargement...</div>;
    }

    /**
     * Specific action executed when clicking "Retour à l'accueil".
     * - If navigation is locked (unsent data), open confirm modal.
     * - Otherwise, navigate directly to home.
     */
    const handleSpecificBackToHome = () => {
        if (lockNavigation) {
            setConfirmHomeOpen(true);
            return;
        }
        navigate('/');
    };

    // State to hold folder data
    return (
        <React.Fragment>
            <Container
                className="w-100 folder-container"
                title={title || ''}
                titleBackground={color || 'transparent'}
            >
                <div
                    ref={overviewRef}
                    className="overview-container"
                    id="folder-overview"
                >
                    {/* Folder Overview Page */}
                    <h1 className="folder-container--title">Rapport d'intervention N° {refFolder}</h1>

                    {/* Client Information Section */}
                    <ClientInfo 
                        enabledMapButtons={false}
                    />

                    {/* Folder Information Section */}
                    <FolderInfo />

                    {/* Appliance Information Section */}
                    <ApplianceInfo />

                    {/* Warranty Information Section */}
                    <WarrantyInfo />

                    {/* Declared and Observed Issues Section */}
                    <DeclaredIssueInfo />

                    {/* Additional Information Section */}
                    <AdditionalInfo />

                    {/* Appointments Section */}
                    <AppointmentsInfo />

                    {/* Spare Parts Section */}
                    <PartsInfo />

                    {/* Payments Section */}
                    <PaymentsOverview/>

                    {/* Overview Tools */}
                    <OverviewTools overviewRef={overviewRef} />
                </div>

                {/* Confirm leave modal */}
                <ConfirmSendFolderModal
                    open={confirmHomeOpen}
                    onClose={() => setConfirmHomeOpen(false)}
                />

                {/* Navigate Footer */}
                <NavigateFooter
                    onBack={() => navigate(`/folders/${refFolder}/payments-and-signature`)}
                    backToHome={ true }
                    specificActionBackToHome={handleSpecificBackToHome}
                />
            </Container>
        </React.Fragment>
    );
};

export default FolderOverviewPage;